import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchPlaceSuggestionResults,
  getPlaceSuggestionRequestKey,
  MIN_PLACE_SUGGESTION_SEARCH_LENGTH,
} from "./place-suggestion-service";

export const usePlaceSuggestionResults = ({
  trimmedSearch,
  nearbyOnly,
  userLocation,
}) => {
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const requestIdRef = useRef(0);
  const activeSearchKeyRef = useRef("");
  const suggestionsCacheRef = useRef(new Map());

  const effectiveUserLocation = nearbyOnly ? userLocation : null;

  const readCachedSuggestions = useCallback((requestKey) => {
    const cached = suggestionsCacheRef.current.get(requestKey);
    return Array.isArray(cached) ? cached : [];
  }, []);

  const writeCachedSuggestions = useCallback((requestKey, suggestions) => {
    if (!Array.isArray(suggestions) || suggestions.length === 0) return;
    suggestionsCacheRef.current.set(requestKey, suggestions);
  }, []);

  const fetchPlaceSuggestions = useCallback(
    async (
      searchTerm,
      { updateState = true, includeExecutionMeta = false } = {}
    ) => {
      const nextSearch = String(searchTerm || "").trim();

      if (
        !nextSearch ||
        nextSearch.length < MIN_PLACE_SUGGESTION_SEARCH_LENGTH
      ) {
        if (updateState) {
          setPlaceSuggestions([]);
          setIsSuggestionLoading(false);
        }
        return [];
      }

      const requestKey = getPlaceSuggestionRequestKey({
        searchTerm: nextSearch,
        nearbyOnly,
        userLocation: effectiveUserLocation,
      });
      const getFallbackSuggestions = () => readCachedSuggestions(requestKey);

      const requestId = updateState
        ? requestIdRef.current + 1
        : requestIdRef.current;
      if (updateState) {
        requestIdRef.current = requestId;
        activeSearchKeyRef.current = requestKey;

        const cachedSuggestions = getFallbackSuggestions();
        if (cachedSuggestions.length > 0) {
          setPlaceSuggestions(cachedSuggestions);
        }
        setIsSuggestionLoading(true);
      }

      const canUpdateState = () =>
        updateState &&
        requestId === requestIdRef.current &&
        requestKey === activeSearchKeyRef.current;

      try {
        const response = await fetchPlaceSuggestionResults({
          searchTerm: nextSearch,
          nearbyOnly,
          userLocation: effectiveUserLocation,
          maxResults: 4,
          includeExecutionMeta,
        });
        const suggestions = response.placeSuggestions;

        if (suggestions.length > 0) {
          writeCachedSuggestions(requestKey, suggestions);
        }

        const visibleSuggestions =
          suggestions.length > 0 ? suggestions : getFallbackSuggestions();

        if (canUpdateState()) {
          setPlaceSuggestions(visibleSuggestions);
        }

        return visibleSuggestions;
      } catch (error) {
        const visibleSuggestions = getFallbackSuggestions();
        if (canUpdateState()) {
          setPlaceSuggestions(visibleSuggestions);
        }
        return visibleSuggestions;
      } finally {
        if (canUpdateState()) {
          setIsSuggestionLoading(false);
        }
      }
    },
    [
      nearbyOnly,
      effectiveUserLocation?.lat,
      effectiveUserLocation?.lng,
      readCachedSuggestions,
      writeCachedSuggestions,
    ]
  );

  useEffect(() => {
    const requestKey = getPlaceSuggestionRequestKey({
      searchTerm: trimmedSearch,
      nearbyOnly,
      userLocation: effectiveUserLocation,
    });
    activeSearchKeyRef.current = requestKey;

    if (
      !trimmedSearch ||
      trimmedSearch.length < MIN_PLACE_SUGGESTION_SEARCH_LENGTH
    ) {
      requestIdRef.current += 1;
      setPlaceSuggestions([]);
      setIsSuggestionLoading(false);
      return;
    }

    const cachedSuggestions = readCachedSuggestions(requestKey);
    setPlaceSuggestions(cachedSuggestions);
    setIsSuggestionLoading(true);
    const timeout = setTimeout(() => {
      fetchPlaceSuggestions(trimmedSearch);
    }, 180);
    const loadingTimeout = setTimeout(() => {
      if (activeSearchKeyRef.current === requestKey) {
        setIsSuggestionLoading(false);
      }
    }, 4000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(loadingTimeout);
    };
  }, [
    trimmedSearch,
    nearbyOnly,
    effectiveUserLocation?.lat,
    effectiveUserLocation?.lng,
    fetchPlaceSuggestions,
    readCachedSuggestions,
  ]);

  return {
    placeSuggestions,
    topPlace: placeSuggestions?.[0],
    isSuggestionLoading,
    fetchPlaceSuggestions,
  };
};
